import { useEffect } from 'react';
import { HttpError } from '../core/http';
import { authService } from '../services';
import { notifySessionExpired } from '../utils/authSessionHandler';
import { secureStorage } from '../utils/secureStorage';

function isHttpError(error: unknown): error is HttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as HttpError).message === 'string'
  );
}

/**
 * Hook para renovar automaticamente o token quando próximo do vencimento
 */
export const useTokenRefresh = () => {
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      try {
        const hasStoredToken = Boolean(secureStorage.getItem('access_token'));

        if (hasStoredToken && !authService.isAuthenticated()) {
          notifySessionExpired('token_expired');
          return;
        }

        if (authService.isAuthenticated() && authService.isTokenNearExpiry()) {
          try {
            const refreshResponse = await authService.refreshToken();
            if (refreshResponse.access_token) {
              secureStorage.setItem(
                'access_token',
                refreshResponse.access_token
              );
              secureStorage.setItem('token_type', refreshResponse.token_type);
              secureStorage.setItem(
                'expires_in',
                refreshResponse.expires_in.toString()
              );
              secureStorage.setItem('token_created_at', Date.now().toString());
              if (refreshResponse.refresh_token) {
                secureStorage.setItem(
                  'refresh_token',
                  refreshResponse.refresh_token
                );
              }
            }
          } catch (error) {
            if (isHttpError(error) && error.status === 401) {
              notifySessionExpired('token_expired');
            }
          }
        }
      } catch {
        // Ignore transient token check errors.
      }
    };

    void checkAndRefreshToken();

    const interval = setInterval(() => {
      void checkAndRefreshToken();
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);
};
